import React, { useEffect } from 'react';
import Cookies from 'cookies';
import dynamic from 'next/dynamic';
import { GetServerSidePropsContext } from 'next';
import { COOKIE_KEY } from 'src/constants';
import { ISection, IResponse, IPageData, IContent } from 'src/interface';
import { trackingPage } from 'src/utils/tracking';
import PageService from 'src/services/PageService';
import { renderSections } from 'src/modules/shared/components/sections';
import ContentMoreSection from 'src/modules/content/components/sections/ContentMoreSection';
import { ZShortSection } from 'src/modules/event/zshort';
import { HotTagSection } from 'src/modules/home/sections/tag';
import { UtilitiesSection } from 'src/modules/home/sections/utilities';
import { ContentTopicHomeSection } from 'src/modules/home/sections/topic';

import './style.module.scss';

export enum SECTION_ID {
    HOME_MORE = 300,
    BANNER = 140,
    ZSHORT = 142,
    CONTENT_TAGS = 60,
    UTILITIES = 143,
    CONTENT_BY_TOPIC = 80,
}

const MoreSection = dynamic(
    () => import('src/modules/shared/components/sections/MoreSection'),
    {
        ssr: false,
    },
);

export const SECTION_MAP_HOME: any = {
    [SECTION_ID.HOME_MORE]: MoreSection,
    [SECTION_ID.ZSHORT]: ZShortSection,
    [SECTION_ID.CONTENT_TAGS]: HotTagSection,
    [SECTION_ID.UTILITIES]: UtilitiesSection,
    [SECTION_ID.CONTENT_BY_TOPIC]: ContentTopicHomeSection,
};

function buildSections(
    sections: ISection<any>[],
    contents: IContent[],
    contentReadIds: number[],
): ISection<any>[] {
    let _contents: IContent[] = [];
    let index = 0;
    if (contents && contents.length > 0) {
        _contents = contents.filter((item) => {
            return !contentReadIds.includes(item.contentId) || item.isNoFilter;
        });

        for (let i = 0; i < _contents.length; i++) {
            _contents[i].index = i;
            if (index < 7) {
                _contents[i].customTopic = 'BM_wap_top01';
            } else {
                if (index < 17) {
                    _contents[i].customTopic = 'BM_wap_top02';
                }
            }
            index++;
        }
    }

    if (_contents && _contents.length > 0) {
        let startIndex = 0;

        for (let i = 0; i < sections.length; i++) {
            let section = sections[i];
            if (section.itemSize && section.itemSize > 0) {
                section.items = _contents.slice(
                    startIndex,
                    startIndex + section.itemSize,
                );
                startIndex += section.itemSize;
                sections[i] = section;
            }
        }
    }

    return sections;
}

export function Home({
    resp,
    isMobile = true,
    isBot = false,
    contentReadIds = [],
}: {
    resp: IResponse<IPageData<IData>>;
    isMobile: boolean;
    isBot: boolean;
    contentReadIds: number[];
}) {
    const content = resp?.data?.content || ({} as any);
    useEffect(() => {
        if (resp) {
            setTimeout(() => {
                trackingPage('mobile_home');
                (window as any).firstLoad = false;
            }, 100);
        }
    }, [resp]);

    if (isMobile) {
        let sections: ISection<any>[];
        if (content.isVeryLargeReq || content.page > 1) {
            buildSections(
                content.sections[0].items,
                content.items,
                contentReadIds,
            );
            sections = content.sections;
        } else {
            sections = buildSections(
                content.sections,
                content.items,
                contentReadIds,
            );
        }

        return <>{renderSections(sections, SECTION_MAP_HOME, isBot)}</>;
    }

    return (
        <ContentMoreSection {...content} isMobile={isMobile} typeList="rank" />
    );
}

interface IData extends ISection<IContent> {
    sections: ISection<any>[];
    items: IContent[];
    contentReadIds: number[];
    isVeryLargeReq: boolean;
    page: number;
}

export async function getServerSideProps(
    ctx: GetServerSidePropsContext,
): Promise<any> {
    const cookies = new Cookies(ctx.req, ctx.res);
    let contentReads = cookies.get(COOKIE_KEY.BM_CONTENT_READ);

    let contentReadIds: number[] = [];
    if (contentReads) {
        contentReadIds = contentReads
            .toString()
            .split(',')
            ?.map((strId: string) => parseInt(strId, 10));
    }

    return PageService.get(ctx, 'home', { page: ctx.params?.page || 1 }).then(
        (resp: any) => {
            if (resp.props) {
                resp.props.contentReadIds = contentReadIds;
            }
            return {
                props: resp.props || {},
            };
        },
    );
}

export default Home;
